import React, { useState, useEffect } from "react";
import Header from "../../components/common/Header";
import jwt_decode from "jwt-decode";
import Icon from "../../components/common/Icon";
import {getSingleCategoryImageList, uploadDeceasedDefaultImage, deleteSingleImageFromDb} from "../../api/admin/imageReq";
import useAuth from "../../hooks/useAuth";
import useData from "../../hooks/useData";
import {getAgencyDefaultDeceasedImage, updatetAgencyDefaultDeceasedImage} from "../../api/pro/agencyReq";
import imageCompression from "browser-image-compression";
import {fileToBase64, getErrorListFromValidation, removeErrorList} from "../../helper/tools";

export default function AgencyDeceasedPhotos() {
    const deceasedCategory = "1";
    const sources = "fa fa-image";
    const { auth } = useAuth();
    const token = auth.token;
    const decodedToken = jwt_decode(token);
    const agencyId = decodedToken.agency;
    const imageFolder = process.env.REACT_APP_LOCAL_ASSETS_BASE_URL;
    const [ deceasedImageLib, setDeceasedImageLib ] = useState([]);
    const { agencyDefaultDeceasedImage, setAgencyDefaultDeceasedImage } = useData();
    const [ enabledDeceasedImageLib, setEnabledDeceasedImageLib ] = useState([]);
    const [ rootImageList, setRootImageList ] = useState([]);
    const [ agencyImageList, setAgencyImageList ] = useState([]);
    const [ showModal, setShowModal ] = useState(false);
    const [ selectedCompressedFile, setSelectedCompressedFile ] = useState(null);
    const [ fileName, setFileName ] = useState("");
    const [ selectedImage, setSelectedImage ] = useState(null);
    const [ uploading, setUploading ] = useState(false);
    const [ showEditModal, setShowEditModal ] = useState(false);
    const [ editingImage, setEditingImage ] = useState(null);
    const [ editValues, setEditValues ] = useState({ title: "", keyword: "", alt: "" });
    const [ addValues, setAddValues ] = useState({ title: "", keyword: "", alt: "" });

    const apiDefaultImageLibrary = async () => {
        const response = await getSingleCategoryImageList(token, deceasedCategory);
        setDeceasedImageLib(response);
    }

    const apiDefaultImage = async () => {
        const response = await getAgencyDefaultDeceasedImage(token, agencyId)

        // Gérer le cas où l'API retourne un tableau ou un objet
        let data;
        if (Array.isArray(response)) {
            data = response.length > 0 ? response[0] : {};
        } else {
            data = response || {};
        }

        // Normaliser la propriété defaultImage si elle a un nom différent
        if (data && !data.defaultImage) {
            if (data.image) {
                data = { ...data, defaultImage: data.image };
            } else if (data.default_image) {
                data = { ...data, defaultImage: data.default_image };
            }
        }

        setAgencyDefaultDeceasedImage(data);
    }

    useEffect(() => {
        if (deceasedImageLib) {
            const filtered = deceasedImageLib.filter(image => {
                return image?.enabled === 1 || image?.enabled === true || image?.enabled === "1";
            });
            setEnabledDeceasedImageLib(filtered);
        }
    }, [ deceasedImageLib ]);

    useEffect(() => {
        if (enabledDeceasedImageLib && enabledDeceasedImageLib.length > 0) {
            const rootImages = [];
            const agencyImages = [];

            enabledDeceasedImageLib.forEach(image => {
                rootImages.push({
                    ...image,
                    path: `${imageFolder}/img/deceased_library/${image?.url}`,
                    source: 'root',
                    uniqueKey: `root-${image.id}`
                });
                agencyImages.push({
                    ...image,
                    path: `${imageFolder}/img/deceased_library/${agencyId}/${image?.url}`,
                    source: 'agency',
                    uniqueKey: `agency-${agencyId}-${image.id}`
                });
            });

            setRootImageList(rootImages);
            setAgencyImageList(agencyImages);
        }
    }, [ enabledDeceasedImageLib, agencyId ]);

    const handleChooseDefaultImage = async (url) => {
        const body = {
            agencyId: agencyId,
            image: url
        }
        await updatetAgencyDefaultDeceasedImage(token, body);
        await apiDefaultImage();
    }

    const handleEditImage = async (imageId, imageUrl) => {
        // Récupérer les détails de l'image depuis la liste
        const imageDetails = deceasedImageLib.find(img => img.id === imageId);
        
        setEditingImage({ id: imageId, url: imageUrl });
        setEditValues({
            title: imageDetails?.title || "",
            keyword: imageDetails?.keyword || "",
            alt: imageDetails?.alt || ""
        });
        
        // Charger l'aperçu de l'image actuelle
        const currentImagePath = `${imageFolder}/img/deceased_library/${agencyId}/${imageUrl}`;
        setSelectedImage(currentImagePath);
        
        setShowEditModal(true);
    }

    const handleCloseEditModal = () => {
        setShowEditModal(false);
        setEditingImage(null);
        setEditValues({ title: "", keyword: "", alt: "" });
        setSelectedCompressedFile(null);
        setFileName("");
        setSelectedImage(null);
    };

    const handleSaveEditImage = async () => {

        setUploading(true);

        const formData = new FormData();
        if (selectedCompressedFile) {
            formData.append('file', selectedCompressedFile);
        }
        formData.append('file_type', 'image');
        formData.append('category', deceasedCategory);
        formData.append('imageId', editingImage.id);
        formData.append('agencyId', agencyId);
        formData.append('title', editValues.title || '');
        formData.append('keyword', editValues.keyword || '');
        formData.append('alt', editValues.alt || '');
        formData.append('fileName', fileName || editingImage.url);

        try {
            const response = await uploadDeceasedDefaultImage(token, formData);
            removeErrorList();

            if (!response?.response?.data?.error) {
                await apiDefaultImageLibrary();
                handleCloseEditModal();
                alert("Image modifiée avec succès");
            } else {
                getErrorListFromValidation(response?.response?.data?.error);
            }
        } catch (error) {
            console.error("Erreur modification:", error);
            alert("Erreur lors de la modification");
        } finally {
            setUploading(false);
        }
    }

    const handleDeleteImage = async (imageId) => {
        let body = {
            imageId: imageId,
            category: 1,
            agencyId: agencyId
        }
        const response = await deleteSingleImageFromDb(token, body);
        removeErrorList();

        if (!response?.response?.data?.error) {
            await apiDefaultImageLibrary();
            await apiDefaultImage();
        } else {
            getErrorListFromValidation(response?.response?.data?.error);
        }
    }

    const handleFileChange = async (e) => {
        const file = e.target.files[0];
        if (file) {
            setFileName(file.name);
            const reader = new FileReader();
            reader.onloadend = () => {
                setSelectedImage(reader.result);
            };
            reader.readAsDataURL(file);

            try {
                const options = {
                    maxSizeMB: 1,
                    maxWidthOrHeight: 1920,
                    useWebWorker: true
                };
                const compressedFile = await imageCompression(file, options);
                const base64 = await fileToBase64(compressedFile);
                setSelectedCompressedFile(base64);
            } catch (error) {
                console.error("Erreur lors de la compression:", error);
            }
        }
    };

    const handleEditFileChange = async (e) => {
        const file = e.target.files[0];
        if (file) {
            setFileName(file.name);
            const reader = new FileReader();
            reader.onloadend = () => {
                setSelectedImage(reader.result);
            };
            reader.readAsDataURL(file);

            try {
                const options = {
                    maxSizeMB: 1,
                    maxWidthOrHeight: 1920,
                    useWebWorker: true
                };
                const compressedFile = await imageCompression(file, options);
                const base64 = await fileToBase64(compressedFile);
                setSelectedCompressedFile(base64);
            } catch (error) {
                console.error("Erreur lors de la compression:", error);
            }
        }
    };

    const handleUploadImage = async () => {
        if (!selectedCompressedFile) {
            alert("Veuillez sélectionner une image");
            return;
        }

        setUploading(true);

        const formData = new FormData();
        formData.append('file', selectedCompressedFile);
        formData.append('file_type', 'image');
        formData.append('category', deceasedCategory);
        formData.append('imageId', 0);
        formData.append('agencyId', agencyId);
        formData.append('title', addValues.title || '');
        formData.append('keyword', addValues.keyword || '');
        formData.append('alt', addValues.alt || '');
        formData.append('fileName', fileName);

        try {
            const response = await uploadDeceasedDefaultImage(token, formData);
            removeErrorList();

            if (!response?.response?.data?.error) {
                await apiDefaultImageLibrary();
                setShowModal(false);
                setSelectedCompressedFile(null);
                setFileName("");
                setSelectedImage(null);
                setAddValues({ title: "", keyword: "", alt: "" });
            } else {
                getErrorListFromValidation(response?.response?.data?.error);
            }
        } catch (error) {
            console.error("Erreur upload:", error);
        } finally {
            setUploading(false);
        }
    };

    const handleOpenModal = () => {
        setShowModal(true);
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setSelectedCompressedFile(null);
        setFileName("");
        setSelectedImage(null);
        setAddValues({ title: "", keyword: "", alt: "" });
    };

    useEffect(() => {
        apiDefaultImageLibrary()
        apiDefaultImage()
    }, [ agencyId ]);


    return (
        <main>
            <Header
                location="Photos comptes défunts"
                parent="Tableau de bord"
                slug="/"
            />
            <div className="content-container">
                <Icon source={sources} span=" Photos comptes défunts "/>
                <div className="dashboard">
                    <p>Sélectionnez les images par défaut que vous souhaitez voir apparaître sur
                        la page du défunt, si aucune photo n'est définie sur le défunt :
                    </p>

                    <button className="green-button" onClick={handleOpenModal}>
                        <i className="fa fa-plus"></i>
                        <span>Ajouter une photo</span>
                    </button>

                    <h3 className="agency-photos-section-title agency-photos-section-title-first">
                        Images de l'agence
                    </h3>
                    {agencyImageList?.length > 0 ?
                            <div className="images-div">
                                {agencyImageList?.map((imageDeceased) => {
                                    const isSelected = agencyDefaultDeceasedImage?.defaultImage === imageDeceased?.url;
                                    return (
                                <div key={imageDeceased.uniqueKey} className={`image ${isSelected ? "selected" : ""}`}>
                                    <i className="fa fa-check"/>
                                    <div className="overlay">
                                        <div className="column">
                                            <div
                                                onClick={() => handleChooseDefaultImage(imageDeceased?.url)}>
                                                <div className="flex">
                                                    <i className="fa fa-check"></i>
                                                    <span>Sélectionner</span>
                                                </div>
                                            </div>
                                            <div className="flex">
                                                <i
                                                    className="fa fa-pen-to-square"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleEditImage(imageDeceased?.id, imageDeceased?.url);
                                                    }}
                                                    title="Modifier l'image"
                                                ></i>
                                                <i
                                                    className="fa fa-trash"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleDeleteImage(imageDeceased?.id);
                                                    }}
                                                    title="Supprimer l'image"
                                                ></i>
                                            </div>
                                        </div>
                                    </div>
                                    <img
                                        src={imageDeceased.path}
                                        className='portrait'
                                        alt={imageDeceased?.alt || "Portrait défunt"}
                                        onError={(e) => {
                                            e.target.style.display = 'none';
                                            e.target.parentElement.style.display = 'none';
                                        }}
                                    />
                                </div>
                                )})}
                        </div>
                        :
                        <div className="flex">
                            <p>Pas d'image dans cette bibliothèque.</p>
                        </div>
                    }

                    <h3 className="agency-photos-section-title">
                        Images communes (admin)
                    </h3>
                    {rootImageList?.length > 0 ?
                        <>
                            <div className="images-div">
                                {rootImageList?.map((imageDeceased) => {
                                    const isSelected = agencyDefaultDeceasedImage?.defaultImage === imageDeceased?.url;
                                    return (
                                    <div key={imageDeceased.uniqueKey} className={`image ${isSelected ? "selected" : ""}`}>
                                        <i className="fa fa-check"/>
                                        <div className="overlay">
                                            <div className="column">
                                                <div
                                                    onClick={() => handleChooseDefaultImage(imageDeceased?.url)}>
                                                    <div className="flex">
                                                        <i className="fa fa-check"></i>
                                                        <span>Sélectionner</span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        <img
                                            src={imageDeceased.path}
                                            className='portrait'
                                            alt={imageDeceased?.alt || "Portrait défunt"}
                                            onError={(e) => {
                                                e.target.style.display = 'none';
                                                e.target.parentElement.style.display = 'none';
                                            }}
                                        />
                                    </div>
                                )})}
                            </div>
                        </>
                        :
                        <div className="flex">
                            <p>Pas d'image dans cette bibliothèque.</p>
                        </div>
                    }
                </div>
            </div>

            {showModal && (
                <div className="modal-overlay modal-overlay-secondary">
                    <div className="modal-content modal-edit-image" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2>Ajouter une photo</h2>
                            <i className="fa fa-times close-icon" onClick={handleCloseModal}></i>
                        </div>
                        <div className="modal-body">
                            <div className="form-input">
                                <label htmlFor="image">Sélectionner une image (requis)</label>
                                <input
                                    type="file"
                                    name="image"
                                    accept="image/*"
                                    id="image"
                                    onChange={handleFileChange}
                                />
                            </div>
                            {selectedImage && (
                                <div className="preview-container">
                                    <p className="preview-label">Aperçu :</p>
                                    <img
                                        src={selectedImage}
                                        alt="Aperçu"
                                        className="preview-image"
                                    />
                                </div>
                            )}
                        </div>
                        <div className="errors"></div>
                        <div className="buttons-container">
                            <button
                                type="button"
                                className="red-button"
                                onClick={handleCloseModal}
                                disabled={uploading}
                            >
                                <i className="fa fa-times"></i>
                                <span>Annuler</span>
                            </button>
                            {/* Masqué (plutôt que grisé) tant qu'aucune image n'est sélectionnée :
                                rien à enregistrer, donc pas de bouton "Enregistrer" à proposer. */}
                            {selectedCompressedFile &&
                                <button
                                    type="button"
                                    className="green-button"
                                    onClick={handleUploadImage}
                                    disabled={uploading}
                                >
                                    <i className="fa fa-check"></i>
                                    <span>{uploading ? "Upload en cours..." : "Enregistrer"}</span>
                                </button>
                            }
                        </div>
                    </div>
                </div>
            )}

            {showEditModal && (
                <div className="modal-overlay modal-overlay-secondary">
                    <div className="modal-content modal-edit-image" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2>Modifier l'image</h2>
                            <i className="fa fa-times close-icon" onClick={handleCloseEditModal}></i>
                        </div>
                        <div className="modal-body">
                            <div className="form-input">
                                <label htmlFor="edit-title">Titre de l'image</label>
                                <input
                                    type="text"
                                    name="title"
                                    id="edit-title"
                                    value={editValues.title}
                                    onChange={(e) => setEditValues({ ...editValues, title: e.target.value })}
                                    placeholder="Titre de l'image"
                                />
                            </div>
                            <div className="form-input">
                                <label htmlFor="edit-keyword">Mot-clé</label>
                                <input
                                    type="text"
                                    name="keyword"
                                    id="edit-keyword"
                                    value={editValues.keyword}
                                    onChange={(e) => setEditValues({ ...editValues, keyword: e.target.value })}
                                    placeholder="Mot-clé de l'image"
                                />
                            </div>
                            <div className="form-input">
                                <label htmlFor="edit-alt">Description (alt)</label>
                                <input
                                    type="text"
                                    name="alt"
                                    id="edit-alt"
                                    value={editValues.alt}
                                    onChange={(e) => setEditValues({ ...editValues, alt: e.target.value })}
                                    placeholder="Courte description de l'image"
                                />
                            </div>
                            <div className="form-input">
                                <label htmlFor="edit-image">Remplacer l'image (optionnel)</label>
                                <input
                                    type="file"
                                    name="image"
                                    accept="image/*"
                                    id="edit-image"
                                    onChange={handleEditFileChange}
                                />
                            </div>
                            {selectedImage && (
                                <div className="preview-container">
                                    <p className="preview-label">
                                        {selectedCompressedFile ? "Nouvelle image :" : "Image actuelle :"}
                                    </p>
                                    <img
                                        src={selectedImage}
                                        alt="Aperçu"
                                        className="preview-image"
                                        onError={(e) => {
                                            // Si l'image n'existe pas dans le dossier agence, essayer root
                                            if (e.target.src.includes(`/${agencyId}/`)) {
                                                e.target.onerror = null; // Éviter la boucle infinie
                                                e.target.src = `${imageFolder}/img/deceased_library/${editingImage?.url}`;
                                            }
                                        }}
                                    />
                                </div>
                            )}
                        </div>
                        <div className="errors"></div>
                        <div className="buttons-container">
                            <button
                                type="button"
                                className="red-button"
                                onClick={handleCloseEditModal}
                                disabled={uploading}
                            >
                                <i className="fa fa-times"></i>
                                <span>Annuler</span>
                            </button>
                            <button
                                type="button"
                                className="green-button"
                                onClick={handleSaveEditImage}
                                disabled={uploading}
                            >
                                <i className="fa fa-check"></i>
                                <span>{uploading ? "Enregistrement..." : "Enregistrer"}</span>
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </main>
    )
}
